import { Eye, EyeOff, LockKeyhole, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { AccessPage } from './AccessPage';
import { AlertsPage } from './AlertsPage';
import { AudienceRecordsSection } from './AudienceRecordsSection';
import { AutomationsPage } from './AutomationsPage';
import { ChatBotPage } from './ChatBotPage';
import { CommandsPage } from './CommandsPage';
import { ControlRoomPage } from './ControlRoomPage';
import { IntegrationsPage } from './IntegrationsPage';
import { LogsPage } from './LogsPage';
import { ModulesPage } from './ModulesPage';
import { LoyaltyPage } from './LoyaltyPage';
import { ObsPage } from './ObsPage';
import { PrinterPage } from './PrinterPage';
import { PrivateChatChimeControl } from './PrivateChatChimeControl';
import { usePrivateChatChime } from './private-chat-chime';
import { RebuildShell } from './RebuildShell';
import { ReportsPage } from './ReportsPage';
import { RaidFinderPage } from './RaidFinderPage';
import { SoundsPage } from './SoundsPage';
import { StartStreamPage } from './StartStreamPage';
import { StreamConfigurationSection } from './StreamConfigurationSection';
import { SystemHealthPage } from './SystemHealthPage';
import { TextOutputsPage } from './TextOutputsPage';
import { ThemesPage } from './ThemesPage';
import { ViewersPage } from './ViewersPage';
import { WamPage } from './WamPage';
import { WeekendPrepPage } from './WeekendPrepPage';
import { WelcomesPage } from './WelcomesPage';
import {
  headerFromControlRoom,
  loadControlRoom,
  loadHeaderState,
  type ControlRoomSnapshot,
  type HeaderSnapshot,
} from './api';
import { REBUILD_NAVIGATION, type RebuildRouteId } from './navigation';
import './bot-workspaces.css';
import './rebuild-auth.css';
import './weekend-prep.css';
import './ffs-visual-language.css';

type Session = {
  loading: boolean;
  authenticated: boolean;
  username: string | null;
  role: 'owner' | 'operator' | null;
};

const VALID_ROUTES = new Set<RebuildRouteId>(
  REBUILD_NAVIGATION.flatMap((group) => group.items.map((item) => item.id)),
);

function routeFromHash(): RebuildRouteId {
  const candidate = window.location.hash.replace(/^#\/?/, '').trim() as RebuildRouteId;
  return VALID_ROUTES.has(candidate) ? candidate : 'home';
}

function routeAllowed(route: RebuildRouteId, role: Session['role']): boolean {
  const item = REBUILD_NAVIGATION.flatMap((group) => group.items).find((entry) => entry.id === route);
  return Boolean(item && (!item.ownerOnly || role === 'owner'));
}

function formatClock(date: Date): string {
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
}

function identityLabel(role: Session['role']): string {
  return role === 'owner'
    ? 'MutedFFS - Owner'
    : 'ParadoxPixels - Lead Moderator';
}

export function RebuildApp() {
  const [session, setSession] = useState<Session>({
    loading: true,
    authenticated: false,
    username: null,
    role: null,
  });
  const [route, setRoute] = useState<RebuildRouteId>(routeFromHash);
  const [weekendPrepExpanded, setWeekendPrepExpanded] = useState(false);
  const [headerState, setHeaderState] = useState<HeaderSnapshot | null>(null);
  const [controlRoom, setControlRoom] = useState<ControlRoomSnapshot | null>(null);
  const [controlRoomError, setControlRoomError] = useState('');
  const [controlRoomLoading, setControlRoomLoading] = useState(true);
  const [clock, setClock] = useState(new Date());
  const privateChatChime = usePrivateChatChime(session.authenticated);

  const checkSession = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/auth/session', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const payload = (await response.json()) as {
        authenticated?: boolean;
        user?: { username?: string; role?: 'owner' | 'operator' };
        owner?: { username?: string };
      };
      const authenticated = payload.authenticated === true;
      setSession({
        loading: false,
        authenticated,
        username: payload.user?.username ?? payload.owner?.username ?? null,
        role: payload.user?.role ?? (payload.owner?.username ? 'owner' : null),
      });
    } catch {
      setSession({ loading: false, authenticated: false, username: null, role: null });
    }
  }, []);

  const refreshVisibleState = useCallback(async () => {
    if (!session.authenticated) return;

    if (route === 'home') {
      try {
        const snapshot = await loadControlRoom();
        setControlRoom(snapshot);
        setHeaderState(headerFromControlRoom(snapshot));
        setControlRoomError('');
      } catch (caught) {
        setControlRoomError(
          caught instanceof Error ? caught.message : 'Control Room could not be loaded.',
        );
      } finally {
        setControlRoomLoading(false);
      }
      return;
    }

    try {
      setHeaderState(await loadHeaderState());
    } catch {
      // Keep the last known shell state. Individual pages surface their own errors.
    }
  }, [route, session.authenticated]);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!session.authenticated) return;
    if (!routeAllowed(route, session.role)) navigate('home');
  }, [route, session.authenticated, session.role]);

  useEffect(() => {
    if (!session.authenticated) return;
    void refreshVisibleState();
    const timer = window.setInterval(() => void refreshVisibleState(), 5000);
    return () => window.clearInterval(timer);
  }, [refreshVisibleState, session.authenticated]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  function navigate(next: RebuildRouteId) {
    if (!routeAllowed(next, session.role)) return;
    setRoute(next);
    const nextHash = `#/${next}`;
    if (window.location.hash !== nextHash) window.location.hash = nextHash;
  }

  async function logout() {
    await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'same-origin' });
    setHeaderState(null);
    setControlRoom(null);
    setControlRoomError('');
    setControlRoomLoading(true);
    setSession({ loading: false, authenticated: false, username: null, role: null });
  }

  function renderPage() {
    if (route === 'home') {
      return (
        <div className="ffs-combined-pages ffs-control-room-workspace">
          <ControlRoomPage
            snapshot={controlRoom}
            error={controlRoomError}
            loading={controlRoomLoading}
            onRefresh={() => void refreshVisibleState()}
          />
          <section className="ffs-control-weekend" aria-label="Weekend preparation">
            <button type="button" className="ffs-control-weekend-toggle" aria-expanded={weekendPrepExpanded} aria-controls="ffs-control-weekend-content" onClick={() => setWeekendPrepExpanded((expanded) => !expanded)}>
              <span><strong>Weekend Prep</strong><small>Friday and weekend readiness, commands and failed-alert replay</small></span>
              <span aria-hidden="true">{weekendPrepExpanded ? '−' : '+'}</span>
            </button>
            {weekendPrepExpanded ? <div id="ffs-control-weekend-content"><WeekendPrepPage /></div> : null}
          </section>
        </div>
      );
    }
    if (route === 'start-stream') return <><StartStreamPage /><StreamConfigurationSection role={session.role ?? 'operator'} /></>;
    if (route === 'themes') return (
      <div className="ffs-combined-pages ffs-themes-workspace">
        <nav className="ffs-feature-jump-links" aria-label="Theme configuration sections">
          <button type="button" onClick={() => document.getElementById('theme-welcomes')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Welcomes</button>
          <button type="button" onClick={() => document.getElementById('theme-alerts')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Alerts &amp; effects</button>
          <button type="button" onClick={() => document.getElementById('theme-halloween')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Gravestones &amp; Ghost Camera</button>
          <button type="button" onClick={() => document.getElementById('theme-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Themes list</button>
        </nav>
        <section id="theme-welcomes" className="ffs-feature-section" aria-label="Welcome presentation and sounds"><WelcomesPage /></section>
        <section id="theme-alerts" className="ffs-feature-section" aria-label="Alerts and effects"><AlertsPage /></section>
        <section id="theme-settings" className="ffs-feature-section" aria-label="Theme settings, Halloween graphics and theme list"><ThemesPage /></section>
      </div>
    );
    if (route === 'weekend-prep') return <WeekendPrepPage />;
    if (route === 'obs') return <ObsPage />;
    if (route === 'reports') return <ReportsPage role={session.role ?? 'operator'} />;
    if (route === 'raid-finder') return <RaidFinderPage />;
    if (route === 'viewers') return (
      <div className="ffs-combined-pages ffs-people-workspace">
        <nav className="ffs-feature-jump-links" aria-label="Viewers and loyalty sections">
          <button type="button" onClick={() => document.getElementById('people-viewers')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Viewers &amp; moderation</button>
          <button type="button" onClick={() => document.getElementById('viewer-history')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Viewer history</button>
          <button type="button" onClick={() => document.getElementById('people-loyalty')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Loyalty &amp; points</button>
          <button type="button" onClick={() => document.getElementById('people-raid')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>Raid preferences</button>
        </nav>
        <section id="people-viewers" className="ffs-feature-section" aria-label="Viewers and moderation"><ViewersPage /></section>
        <section id="people-loyalty" className="ffs-feature-section" aria-label="Loyalty balances, awards and settings"><LoyaltyPage role={session.role ?? 'operator'} /></section>
        <section id="people-raid" className="ffs-feature-section" aria-label="Stored raid preferences"><AudienceRecordsSection showLoyaltyBalances={false} /></section>
      </div>
    );
    if (route === 'loyalty') return <LoyaltyPage role={session.role ?? 'operator'} />;
    if (route === 'welcomes') return <WelcomesPage />;
    if (route === 'alerts') return <AlertsPage />;
    if (route === 'sounds') return <SoundsPage />;
    if (route === 'printer') return <PrinterPage role={session.role ?? 'operator'} />;
    if (route === 'text-outputs') return <TextOutputsPage role={session.role ?? 'operator'} />;
    if (route === 'wam') return <WamPage />;
    if (route === 'chat-bot') return (
      <div className="ffs-combined-pages ffs-chatbot-workspace">
        <nav className="ffs-chatbot-jump-links" aria-label="Chat Bot page sections">
          <a href="#bot-controls" onClick={(event) => { event.preventDefault(); document.getElementById('bot-controls')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>Bot controls</a>
          <a href="#bot-automations" onClick={(event) => { event.preventDefault(); document.getElementById('bot-automations')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>Keywords &amp; notices</a>
          <a href="#bot-commands" onClick={(event) => { event.preventDefault(); document.getElementById('bot-commands')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>Commands</a>
        </nav>
        <section id="bot-controls" aria-label="Bot controls"><ChatBotPage role={session.role ?? 'operator'} /></section>
        <section id="bot-automations" aria-label="Keyword automations and scheduled notices"><AutomationsPage /></section>
        <section id="bot-commands" aria-label="Chat commands"><CommandsPage /></section>
      </div>
    );
    if (route === 'commands') return <CommandsPage />;
    if (route === 'automations') return <AutomationsPage />;
    if (route === 'system-health') return <SystemHealthPage role={session.role ?? 'operator'} />;
    if (route === 'logs') return <LogsPage />;
    if (route === 'integrations') return <IntegrationsPage />;
    if (route === 'modules') return <ModulesPage />;
    if (route === 'access') return <AccessPage />;
    return <ControlRoomPage snapshot={controlRoom} error={controlRoomError} loading={controlRoomLoading} onRefresh={() => void refreshVisibleState()} />;
  }

  if (session.loading) return <RebuildLoadingScreen />;
  if (!session.authenticated) return <RebuildLoginScreen onAuthenticated={checkSession} />;

  return (
    <RebuildShell
      activeRoute={route}
      role={session.role ?? 'operator'}
      identityLabel={identityLabel(session.role)}
      streaming={headerState?.streaming ?? false}
      currentScene={headerState?.scene ?? null}
      currentWamCategory={headerState?.wamCategory ?? null}
      nowPlaying={headerState?.nowPlaying ?? null}
      clockLabel={formatClock(clock)}
      dateLabel={formatDate(clock)}
      sidebarUtility={
        <PrivateChatChimeControl
          enabled={privateChatChime.enabled}
          onToggle={() => void privateChatChime.toggle()}
        />
      }
      onNavigate={navigate}
      onLogout={() => void logout()}
    >
      {renderPage()}
    </RebuildShell>
  );
}

function RebuildLoadingScreen() {
  return (
    <div className="ffs-next-auth-shell">
      <div className="ffs-next-auth-card compact">
        <div className="ffs-next-auth-brand"><strong>FFS</strong><span>Control</span></div>
        <p>Checking session…</p>
      </div>
    </div>
  );
}

function RebuildLoginScreen({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!username.trim() || !password) return;

    setSubmitting(true);
    setError('');
    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ username: username.trim(), password, code: code.trim() }),
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? 'The login was not accepted.');
      }

      setPassword('');
      setCode('');
      await onAuthenticated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Login failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="ffs-next-auth-shell">
      <div className="ffs-next-login-frame">
        <section className="ffs-next-login-intro" aria-label="FFSControl">
          <div className="ffs-next-auth-brand">
            <strong>FFS</strong><span>Control</span>
          </div>
          <div className="ffs-next-login-kicker">PRIVATE STREAM OPERATIONS</div>
          <h1>Your stream control room.</h1>
          <p>
            Sign in to manage stream setup, themes, OBS integration, welcomes,
            alerts, WhatsAMusic and the rest of FFSControl.
          </p>

          <div className="ffs-next-login-security">
            <div>
              <ShieldCheck size={17} />
              <span><strong>Owner protected by 2FA</strong><small>Authenticator or recovery code required.</small></span>
            </div>
            <div>
              <LockKeyhole size={17} />
              <span><strong>Private authenticated session</strong><small>Credentials are not stored in the dashboard.</small></span>
            </div>
          </div>
        </section>

        <form className="ffs-next-login-form" onSubmit={(event) => void submit(event)}>
          <div className="ffs-next-login-heading">
            <small>SIGN IN</small>
            <h2>Open FFSControl</h2>
            <p>Use your FFSControl account credentials.</p>
          </div>

          <label htmlFor="ffs-login-username">
            <span>Username</span>
            <input
              id="ffs-login-username"
              name="username"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="FFSControl username"
            />
          </label>

          <label htmlFor="ffs-login-password">
            <span>Password</span>
            <div className="ffs-next-login-password">
              <input
                id="ffs-login-password"
                name="password"
                autoComplete="current-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Password"
              />
              <button
                type="button"
                className="ffs-next-login-reveal"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword((value) => !value)}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </label>

          <label htmlFor="ffs-login-code">
            <span>Security code <small>Owner only</small></span>
            <input
              id="ffs-login-code"
              name="one-time-code"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="6-digit authenticator or recovery code"
            />
            <small className="ffs-next-login-field-note">
              Lead Moderator accounts leave this blank.
            </small>
          </label>

          {error ? <div className="ffs-next-auth-error" role="alert">{error}</div> : null}

          <button
            type="submit"
            className="ffs-next-login-submit"
            disabled={submitting || !username.trim() || !password}
          >
            {submitting ? 'Signing in…' : 'Sign in to FFSControl'}
          </button>

          <div className="ffs-next-login-footnote">
            FFSControl will not switch OBS scenes just because you sign in.
          </div>
        </form>
      </div>
    </div>
  );
}
